import React, { useEffect, useState, useMemo } from "react";
import { ticketRef } from "utilities/ticketNumber";
import { useNavigate } from "react-router-dom";
import { Ticket as TicketIcon,
  CornerDownRight,
} from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import Skeleton from "components/Skeleton";
import EmptyState from "components/EmptyState";
import RelativeTime from "components/RelativeTime";
import TicketStatusPill from "components/Tickets/TicketStatusPill";

/**
 * EntityTicketsSection (SOUND-497) — reverse-lookup surface.
 * -------------------------------------------------------
 * Renders the tickets linked to a business entity via
 * `GET /tickets?entityType=&entityId=` (scope-enforced). Accepts a single
 * `entityType`+`entityId`, or an `entities` array whose reverse lookups are
 * merged + de-duped by ticketId (used for the Disputifier surface, which spans
 * a company's enrolled MIDs). Reuses TicketStatusPill; rows deep-link to the
 * ticket workspace. `hideWhenEmpty` suppresses the card when there are none.
 */
export default function EntityTicketsSection({
  entityType,
  entityId,
  entities,
  title = "Tickets",
  hideWhenEmpty = false,
  variant = "card", // "card" (full width) | "rail" (320px sidebar)
  className = "",
}) {
  const navigate = useNavigate();
  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const targets = useMemo(() => {
    if (Array.isArray(entities)) return entities.filter((e) => e && e.entityId);
    if (entityType && entityId) return [{ entityType, entityId }];
    return [];
  }, [entities, entityType, entityId]);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const key = targets.map((t) => `${t.entityType}:${t.entityId}`).join("|");

  useEffect(() => {
    if (!auth?.user || !companyId || targets.length === 0) {
      setLoading(false);
      setTickets([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(
      targets.map((t) =>
        makeApiRequest(
          "get",
          apiRoutes.ticketsByEntity(t.entityType, t.entityId),
          auth.user,
          companyId
        )
          .then((res) => res.data?.data || [])
          .catch(() => [])
      )
    ).then((lists) => {
      if (cancelled) return;
      const byId = new Map();
      lists.flat().forEach((tk) => {
        if (tk && tk.ticketId && !byId.has(tk.ticketId)) byId.set(tk.ticketId, tk);
      });
      const merged = Array.from(byId.values()).sort(
        (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
      );
      setTickets(merged);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, companyId]);

  if (loading) {
    if (hideWhenEmpty) return null; // don't flash a card that may then vanish
    return (
      <section className={`surface rounded-xl ring-1 ring-border p-6 ${className}`}>
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
          <TicketIcon className="h-5 w-5 text-brand" />
          {title}
        </h2>
        <Skeleton.Rect className="h-24 w-full" />
      </section>
    );
  }

  if (tickets.length === 0 && hideWhenEmpty) return null;

  // RAIL — mirrors components/Tickets/EntityLinkPanel, the ticket workspace's
  // own "Linked" panel: `surface p-4`, compact uppercase header with a count,
  // bordered rows carrying a truncated title over a small meta line. A record
  // listing its tickets is the reverse of a ticket listing its records, so the
  // two should read identically.
  //
  // The card variant's row puts the status pill and a RelativeTime BESIDE the
  // title behind `hidden sm:inline` — which keys off the VIEWPORT, not the
  // container, so both would show and cramp inside a 320px rail.
  if (variant === "rail") {
    return (
      <section className={`surface p-4 ${className}`}>
        <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <TicketIcon className="h-3.5 w-3.5" />
          Tickets
          {tickets.length > 0 && (
            <span className="text-muted-foreground/70">· {tickets.length}</span>
          )}
        </h3>

        {tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground">No linked tickets.</p>
        ) : (
          <ul className="space-y-1.5">
            {tickets.map((t) => (
              <li key={t.ticketId}>
                <button
                  type="button"
                  onClick={() => navigate(`/tickets/${t.ticketId}`)}
                  className="group flex w-full items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-left transition-colors hover:bg-muted"
                >
                  {/* No per-row ticket icon. The section header already says
                      TICKETS, so repeating the same glyph on every row is the
                      header restated N times — it carries no information and
                      eats the width the title needs. The ticket workspace's own
                      sub-ticket list uses a small directional marker instead,
                      which is what this mirrors. */}
                  <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {t.title}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {ticketRef(t.number)}
                      </span>
                      <TicketStatusPill
                        status={t.status}
                        statusCategory={t.statusCategory}
                      />
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  return (
    <section className={`surface rounded-xl ring-1 ring-border p-6 ${className}`}>
      <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
        <TicketIcon className="h-5 w-5 text-brand" />
        {title}
        {tickets.length > 0 && (
          <span className="text-sm font-normal text-muted-foreground">· {tickets.length}</span>
        )}
      </h2>

      {tickets.length === 0 ? (
        <EmptyState icon={TicketIcon} heading="No linked tickets" description="Tickets linked to this record will appear here." />
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {tickets.map((t) => (
            <button
              key={t.ticketId}
              type="button"
              onClick={() => navigate(`/tickets/${t.ticketId}`)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted"
            >
              <span className="shrink-0 font-mono text-xs text-muted-foreground">{ticketRef(t.number)}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{t.title}</span>
              <TicketStatusPill status={t.status} statusCategory={t.statusCategory} />
              <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                <RelativeTime value={t.updatedAt} />
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
